import React from "react";

import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
} from "@braintrust/braintrust-ui-components";
import type { Passkey } from "@js/apps/auth/api";
import { useUser } from "@js/apps/common/hooks/use-user";

type PasskeyListProps = {
  passkeys: Passkey[];
  onDelete: (id: number) => void;
};

export const PasskeyList = ({ passkeys, onDelete }: PasskeyListProps) => {
  const user = useUser();
  const isLastPasskey = passkeys.length === 1;
  const canDeleteLastPasskey = !!user?.can_disable_passkey?.value;

  if (passkeys.length === 0) {
    return (
      <Typography
        component="p"
        variant="paragraph"
        size="medium"
        color="grey-2"
        sx={{ mb: 2 }}
      >
        No passkeys registered.
      </Typography>
    );
  }

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {passkeys.map((passkey) => {
        return (
          <PasskeyListItem
            key={passkey.id}
            passkey={passkey}
            onDelete={onDelete}
            disableDelete={isLastPasskey && !canDeleteLastPasskey}
          />
        );
      })}
    </Stack>
  );
};

type PasskeyListItemProps = {
  passkey: Passkey;
  onDelete: (id: number) => void;
  disableDelete: boolean;
};

const PasskeyListItem = ({
  passkey,
  onDelete,
  disableDelete,
}: PasskeyListItemProps) => {
  const createdDate = new Date(passkey.created_at).toLocaleDateString();
  const lastUsedDate = passkey.last_used_at
    ? new Date(passkey.last_used_at).toLocaleDateString()
    : null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1.5,
        border: "1px solid var(--soft-beige)",
        borderRadius: "8px",
      }}
    >
      <Box>
        <Typography
          component="p"
          variant="paragraph"
          size="medium"
          fontWeight={500}
        >
          {passkey.name || "Passkey"}
        </Typography>
        <Typography
          component="p"
          variant="paragraph"
          size="small"
          color="grey-2"
        >
          Added {createdDate}
          {lastUsedDate && ` · Last used ${lastUsedDate}`}
        </Typography>
      </Box>
      <Tooltip
        title="Can't remove your only 2FA method while it's required for this account."
        disabled={!disableDelete}
      >
        <span>
          <Button
            variant="secondary"
            size="x-small"
            disabled={disableDelete}
            onClick={() => onDelete(passkey.id)}
          >
            Remove
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};
